/**
 * Android Referrer Redirect Handler
 * Handles Play Store redirects with referrer parameters for Android devices
 */

export const handleAndroidReferrerRedirect = async (i, b, playStoreURL) => {
  try {
    // Validate parameters
    const validation = validateAndroidParameters(i, b, playStoreURL);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Extract package name from Play Store URL
    const packageName = extractPackageName(playStoreURL);
    
    // Create referrer data
    const referrerData = createReferrerData(i, b);
    
    // Construct Play Store URL with referrer
    const playStoreUrlWithReferrer = constructPlayStoreURL(packageName, referrerData);

    console.log('Android redirect URL:', playStoreUrlWithReferrer);

    // Redirect to Play Store (storage API call is handled at component level)
    redirectToPlayStore(playStoreUrlWithReferrer);
    
    return { 
      success: true, 
      url: playStoreUrlWithReferrer,
      message: 'Redirecting to Play Store...' 
    };

  } catch (error) {
    console.error('Android referrer redirect failed:', error);
    
    // Fallback: redirect to Play Store without referrer
    if (playStoreURL) {
      redirectToPlayStore(playStoreURL);
      return {
        success: false,
        error: error.message,
        fallback: true,
        message: 'Redirecting to Play Store (fallback)...'
      };
    }
    
    return { 
      success: false, 
      error: error.message,
      message: 'Redirect failed' 
    };
  }
};

/**
 * Extract package name from Play Store URL
 */
const extractPackageName = (playStoreURL) => {
  // Match patterns like: id=com.example.app or id=com.example.app&other=params
  const packageMatch = playStoreURL.match(/[?&]id=([^&]+)/);
  
  if (packageMatch && packageMatch[1]) {
    return packageMatch[1];
  }
  
  // Fallback to default package name
  console.warn('Could not extract package name from:', playStoreURL);
  return 'com.nivohub.app';
};

/**
 * Create referrer data string with encoded parameters
 */
const createReferrerData = (i, b) => {
  // Encode individual parameters
  const encodedI = encodeURIComponent(i);
  const encodedB = encodeURIComponent(b);
  
  // Create referrer parameter string
  const referrerParams = `i=${encodedI}&b=${encodedB}`;
  
  return referrerParams;
};

/**
 * Construct complete Play Store URL with referrer parameter
 */
const constructPlayStoreURL = (packageName, referrerData) => {
  const baseURL = 'https://play.google.com/store/apps/details';
  const encodedReferrer = encodeURIComponent(referrerData);
  
  return `${baseURL}?id=${packageName}&referrer=${encodedReferrer}`;
};

/**
 * Redirect to Play Store with error handling
 */
const redirectToPlayStore = (url, delay = 0) => {
  const redirect = () => {
    try {
      // Primary method: replace current location
      window.location.href = url;
    } catch (error) {
      console.error('Primary redirect failed:', error);
      try {
        // Fallback 1: assign to location
        window.location.assign(url);
      } catch (fallbackError) {
        console.error('Fallback redirect failed:', fallbackError);
        try {
          // Fallback 2: open in new window
          window.open(url, '_self');
        } catch (finalError) {
          console.error('All redirect methods failed:', finalError);
        }
      }
    }
  };

  if (delay > 0) {
    setTimeout(redirect, delay);
  } else {
    redirect();
  }
};

/**
 * Validate Android deep link parameters
 */
const validateAndroidParameters = (i, b, playStoreURL) => {
  if (!i || !b) {
    return { valid: false, error: 'Missing required parameters i or b' };
  }

  if (!playStoreURL) {
    return { valid: false, error: 'Play Store URL not provided' };
  }

  // Basic UUID validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  if (!uuidRegex.test(i)) {
    return { valid: false, error: 'Parameter i is not a valid UUID' };
  }
  
  if (!uuidRegex.test(b)) {
    return { valid: false, error: 'Parameter b is not a valid UUID' };
  }

  // Validate Play Store URL format
  if (!playStoreURL.includes('play.google.com') && !playStoreURL.includes('market://')) {
    return { valid: false, error: 'Invalid Play Store URL format' };
  }

  return { valid: true };
};

/**
 * Create referrer URL for testing purposes
 */
export const createTestReferrerURL = (packageName, i, b) => {
  const referrerData = createReferrerData(i, b);
  return constructPlayStoreURL(packageName, referrerData);
};

/**
 * Parse referrer data back to parameters (for testing)
 */
export const parseReferrerData = (referrerString) => {
  try {
    const decodedReferrer = decodeURIComponent(referrerString);
    const params = new URLSearchParams(decodedReferrer);
    
    return {
      i: params.get('i'),
      b: params.get('b')
    };
  } catch (error) {
    console.error('Failed to parse referrer data:', error);
    return null;
  }
};
